import React, { useState } from "react";

const Oracle = () => {
  const [expandedSections, setExpandedSections] = useState({});

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const syllabusData = {
    courseOverview: {
      title: "Course Overview",
      content: (
        <>
          <p className="font-bold text-sm">Objective:</p> Master Oracle Database Administration, focusing on installation, configuration, performance tuning, and backup/recovery, preparing for Oracle Certified Professional (OCP) certification.
          <p className="font-bold text-sm">Duration:</p> 45 days, 1 hour per day.
          <p className="font-bold text-sm">Prerequisites:</p> Basic understanding of databases and SQL; familiarity with Linux/Windows systems is helpful.
          <p className="font-bold text-sm">Tools Needed:</p> A computer with Oracle Database 19c/21c (or Oracle VirtualBox image), SQL*Plus or SQL Developer, and internet access.
        </>
      ),
    },
    dailySchedule: {
      title: "Daily Schedule",
      days: [
        {
          day: "Day 1: Introduction to Oracle Database",
          objective: "Understand Oracle Database and set up the environment.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Overview of Oracle Database and its architecture.</li>
                <li>Introduction to Oracle certifications (OCP).</li>
                <li>Installing Oracle Database (19c/21c) on a virtual machine.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Install Oracle Database and verify the installation.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 2: Oracle Database Architecture",
          objective: "Learn the core components of Oracle Database.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Memory structures (SGA, PGA).</li>
                <li>Background processes and instance management.</li>
                <li>Physical and logical database structures.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Explore database files and memory usage with SQL*Plus.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 3: SQL Basics – DDL and DML",
          objective: "Learn basic SQL commands for database management.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Creating tables with DDL (CREATE, ALTER, DROP).</li>
                <li>Manipulating data with DML (INSERT, UPDATE, DELETE).</li>
                <li>Querying data with SELECT.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Create a table and insert sample data.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 4: SQL Queries – Joins and Subqueries",
          objective: "Learn advanced SQL querying techniques.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Using JOINs (INNER, LEFT, RIGHT).</li>
                <li>Writing subqueries and correlated subqueries.</li>
                <li>Using aggregate functions (COUNT, SUM, AVG).</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Write a query with a JOIN and subquery.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 5: Managing Users and Privileges",
          objective: "Learn to manage database users and security.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Creating and managing users (CREATE USER).</li>
                <li>Granting and revoking privileges (GRANT, REVOKE).</li>
                <li>Understanding roles and profiles.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Create a user and grant SELECT privileges.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 6: Managing Tablespaces and Datafiles",
          objective: "Learn to manage storage structures.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Understanding tablespaces and datafiles.</li>
                <li>Creating and altering tablespaces.</li>
                <li>Adding and resizing datafiles.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Create a new tablespace and add a datafile.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 7: Managing Schemas and Objects",
          objective: "Learn to manage database objects.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Creating and managing schemas.</li>
                <li>Working with indexes and constraints.</li>
                <li>Renaming and dropping objects.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Create an index and apply a constraint to a table.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 8: Backup and Recovery Concepts",
          objective: "Understand Oracle backup and recovery strategies.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Overview of RMAN (Recovery Manager).</li>
                <li>Types of backups (full, incremental).</li>
                <li>Introduction to recovery scenarios.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Configure RMAN and take a backup.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 9: Performing Backups with RMAN",
          objective: "Learn to perform database backups.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Configuring RMAN settings.</li>
                <li>Performing full and incremental backups.</li>
                <li>Backing up to disk and tape.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Perform a full backup using RMAN.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 10: Performing Recovery with RMAN",
          objective: "Learn to recover a database using RMAN.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Recovering from datafile loss.</li>
                <li>Performing point-in-time recovery.</li>
                <li>Restoring the control file and spfile.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Simulate datafile loss and recover it.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 11: Configuring Automatic Backups",
          objective: "Learn to automate backups with RMAN.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Setting up automatic backups with RMAN.</li>
                <li>Configuring retention policies.</li>
                <li>Monitoring backup jobs.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Schedule an automatic backup job.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 12: Managing Undo Data",
          objective: "Learn to manage undo tablespaces.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Understanding undo management (auto undo mode).</li>
                <li>Monitoring and resizing undo tablespaces.</li>
                <li>Handling undo retention.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Resize an undo tablespace and monitor its usage.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 13: Managing Redo Logs",
          objective: "Learn to manage redo log files.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Understanding redo logs and log switches.</li>
                <li>Adding and dropping redo log groups.</li>
                <li>Monitoring redo log usage.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Add a new redo log group and verify its status.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 14: Performance Tuning – Basics",
          objective: "Learn basic performance tuning techniques.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Using Automatic Workload Repository (AWR).</li>
                <li>Analyzing SQL performance with SQL Trace.</li>
                <li>Identifying bottlenecks.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Generate an AWR report and analyze it.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 15: Performance Tuning – Advanced",
          objective: "Learn advanced performance optimization.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Tuning memory components (SGA, PGA).</li>
                <li>Using Automatic Database Diagnostic Monitor (ADDM).</li>
                <li>Optimizing SQL queries with hints.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Adjust SGA size and test query performance.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 16: Managing Database Security",
          objective: "Learn to secure the Oracle Database.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Implementing data encryption.</li>
                <li>Configuring auditing (standard and fine-grained).</li>
                <li>Managing database privileges and roles.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Enable auditing for a specific table.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 17: Managing Network Configuration",
          objective: "Learn to configure Oracle Net Services.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Understanding listener and tnsnames.ora.</li>
                <li>Configuring database connectivity.</li>
                <li>Troubleshooting network issues.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Configure a listener and test connectivity.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 18: Managing Data Pump Export/Import",
          objective: "Learn to use Data Pump for data migration.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Introduction to Data Pump (expdp, impdp).</li>
                <li>Exporting and importing schemas/tables.</li>
                <li>Managing Data Pump jobs.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Export a schema and import it into another database.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 19: Managing Flashback Technology",
          objective: "Learn to use Flashback for data recovery.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Understanding Flashback features (Query, Drop).</li>
                <li>Configuring Flashback Database.</li>
                <li>Recovering dropped objects.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Recover a dropped table using Flashback Drop.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 20: Managing Resource Manager",
          objective: "Learn to manage resource allocation.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Introduction to Database Resource Manager.</li>
                <li>Creating resource plans and consumer groups.</li>
                <li>Allocating CPU and memory resources.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Create a resource plan and test its effect.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 21: Configuring Automatic Storage Management (ASM)",
          objective: "Learn to configure and manage ASM.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Introduction to ASM and its benefits.</li>
                <li>Setting up an ASM instance.</li>
                <li>Managing disk groups and files.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Create an ASM disk group and add a disk.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 22: Managing ASM – Advanced",
          objective: "Learn advanced ASM operations.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Rebalancing ASM disk groups.</li>
                <li>Adding and dropping disks from ASM.</li>
                <li>Monitoring ASM performance.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Rebalance an ASM disk group after adding a disk.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 23: Configuring High Availability with Data Guard",
          objective: "Learn to configure Oracle Data Guard.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Introduction to Data Guard and standby databases.</li>
                <li>Setting up a physical standby database.</li>
                <li>Switching roles (switchover, failover).</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Configure a standby database and perform a switchover.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 24: Managing Data Guard – Advanced",
          objective: "Learn advanced Data Guard operations.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Configuring Data Guard Broker.</li>
                <li>Monitoring and tuning replication.</li>
                <li>Performing a failover scenario.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Configure Data Guard Broker and test failover.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 25: Managing Real Application Clusters (RAC) – Basics",
          objective: "Learn the basics of Oracle RAC.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Introduction to Oracle RAC and its architecture.</li>
                <li>Understanding cluster components (CRS, voting disks).</li>
                <li>Installing a basic RAC environment.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Set up a simulated RAC environment (if resources allow).</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 26: Managing RAC – Advanced",
          objective: "Learn advanced RAC operations.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Managing RAC instances and services.</li>
                <li>Configuring load balancing and failover.</li>
                <li>Troubleshooting RAC issues.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Test load balancing in a RAC setup.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 27: Managing Partitioning",
          objective: "Learn to manage partitioned tables.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Introduction to table partitioning.</li>
                <li>Creating range, list, and hash partitions.</li>
                <li>Managing partitions (add, drop, merge).</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Create a partitioned table and add a partition.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 28: Managing Materialized Views",
          objective: "Learn to create and manage materialized views.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Introduction to materialized views.</li>
                <li>Creating and refreshing materialized views.</li>
                <li>Optimizing query performance with views.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Create a materialized view and refresh it.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 29: Final Project – Planning",
          objective: "Plan the final project.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min discussion):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Overview of the final project.</li>
                <li>Suggested project ideas (e.g., High Availability Database).</li>
                <li>Planning features and structure.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Create a project plan and outline.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 30: Final Project – Development (Part 1)",
          objective: "Develop the initial setup of the final project.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Setting up the database environment.</li>
                <li>Configuring tablespaces and users.</li>
                <li>Implementing initial backups.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Start building the project infrastructure.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 31: Final Project – Development (Part 2)",
          objective: "Continue developing the final project.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Adding advanced features (e.g., Data Guard, partitioning).</li>
                <li>Configuring performance tuning.</li>
                <li>Testing configurations.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Enhance the project with additional features.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 32: Final Project – Testing and Debugging",
          objective: "Test and debug the final project.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Testing backups and recovery.</li>
                <li>Troubleshooting performance issues.</li>
                <li>Optimizing resource usage.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Test and fix issues in the project.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 33: Final Project – Documentation",
          objective: "Document the final project.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Creating a project README.</li>
                <li>Documenting configurations and scripts.</li>
                <li>Preparing a presentation outline.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Write documentation for the project.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 34: Preparing for OCP Exam – Basics",
          objective: "Review basic administration skills.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Review tablespace and user management.</li>
                <li>Practice backup and recovery tasks.</li>
                <li>Mock scenarios for basics.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Complete a mock basic administration task.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 35: Preparing for OCP Exam – Performance Tuning",
          objective: "Review performance tuning skills.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Review AWR, ADDM, and SQL tuning.</li>
                <li>Practice optimizing memory and queries.</li>
                <li>Mock performance tuning scenarios.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Optimize a query using hints.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 36: Preparing for OCP Exam – High Availability",
          objective: "Review high availability skills.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Review Data Guard and RAC configurations.</li>
                <li>Practice switchover and failover tasks.</li>
                <li>Mock high availability scenarios.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Perform a mock Data Guard switchover.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 37: Final Project – Rehearsal",
          objective: "Rehearse the final project presentation.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Preparing a demo walkthrough.</li>
                <li>Anticipating questions and challenges.</li>
                <li>Practicing the presentation.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Rehearse the project demo.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 38: Final Project – Peer Review",
          objective: "Receive feedback on the final project.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Presenting to peers for feedback.</li>
                <li>Discussing improvements and optimizations.</li>
                <li>Finalizing the project.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Present and refine the project based on feedback.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 39: Exam Strategy and Tips",
          objective: "Learn strategies for the OCP exam.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Understanding the exam format and timing.</li>
                <li>Time management and prioritization tips.</li>
                <li>Reviewing common pitfalls and solutions.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Practice a timed mock exam task.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 40: Mock Exam – Administration",
          objective: "Practice administration tasks for the OCP exam.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Mock scenario: Manage tablespaces and users.</li>
                <li>Reviewing backup and recovery procedures.</li>
                <li>Self-assessment and feedback.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Complete the mock administration task.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 41: Mock Exam – Performance Tuning",
          objective: "Practice performance tuning tasks.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Mock scenario: Optimize a database with AWR/ADDM.</li>
                <li>Reviewing SQL tuning techniques.</li>
                <li>Self-assessment and feedback.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Complete the mock tuning task.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 42: Mock Exam – High Availability",
          objective: "Practice high availability tasks.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Mock scenario: Configure Data Guard or RAC.</li>
                <li>Reviewing failover and switchover procedures.</li>
                <li>Self-assessment and feedback.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Complete the mock high availability task.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 43: Final Project – Final Adjustments",
          objective: "Finalize the final project.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Reviewing project requirements.</li>
                <li>Making final tweaks and optimizations.</li>
                <li>Preparing for presentation day.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Finalize the project setup.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 44: Final Project – Presentation Rehearsal",
          objective: "Rehearse the final project presentation.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Practicing the full demo.</li>
                <li>Anticipating questions from the audience.</li>
                <li>Refining the presentation flow.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Rehearse the presentation with a timer.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 45: Final Project – Presentation",
          objective: "Present the final project.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Delivering the project demo.</li>
                <li>Discussing challenges and solutions.</li>
                <li>Receiving feedback from peers and instructor.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Present the completed project.</li>
              </ul>
            </>
          ),
        },
      ],
    },
    learningOutcomes: {
      title: "Learning Outcomes",
      content: (
        <>
          <p className="text-sm">
            By the end of this course, learners will be able to:
          </p>
          <ul className="list-disc pl-4 text-xs">
            <li>Install, configure, and manage Oracle Databases.</li>
            <li>Perform backups, recovery, and performance tuning.</li>
            <li>Implement high availability with Data Guard and RAC.</li>
            <li>Secure and optimize database operations.</li>
            <li>Prepare for the Oracle Certified Professional (OCP) exam.</li>
          </ul>
        </>
      ),
    },
    finalProjectIdeas: {
      title: "Suggested Final Project Ideas",
      content: (
        <>
          <ul className="list-disc pl-4 text-xs">
            <li>
              <span className="font-bold text-sm">High Availability Database:</span> Configure a primary and standby database using Data Guard with automated backups.
            </li>
            <li>
              <span className="font-bold text-sm">Performance-Optimized Database:</span> Set up a database with partitioned tables, materialized views, and performance tuning.
            </li>
            <li>
              <span className="font-bold text-sm">Multi-Tier Database System:</span> Implement a database with ASM, RAC, and secure user management.
            </li>
          </ul>
        </>
      ),
    },
    additionalNotes: {
      title: "Additional Notes",
      content: (
        <>
          <ul className="list-disc pl-4 text-xs">
            <li>Practice with Oracle’s official documentation and VirtualBox labs.</li>
            <li>Review the OCP exam objectives regularly (available on Oracle’s website).</li>
            <li>Use discussion time for troubleshooting complex scenarios and Q&A.</li>
          </ul>
          <p className="text-sm">
            This syllabus covers essential Oracle Database administration topics over 45 days, preparing learners for certification and professional roles.
          </p>
        </>
      ),
    },
  };

  // Function to handle enrollment (replace with actual enrollment logic)
  const handleEnroll = () => {
    alert("Enroll Now clicked! (Add your enrollment logic here)");
  };

  return (
    <div className="min-h-screen bg-gray-100 font-sans">
      {/* Hero Section */}
      <header className="bg-gradient-to-r from-green-300 to-indigo-600 py-8 text-center">
        <h1 className="text-2xl font-bold mb-1 text-white">Oracle Syllabus</h1>
        <p className="text-sm text-white">
          Master Oracle Database Administration (45 Hours – 1 Hour Per Day)
        </p>
      </header>

      {/* Training Availability Banner */}
      <div className="fixed top-34 left-80v right-4 bg-yellow-200 border border-yellow-400 rounded-lg p-3 text-center shadow-md z-10">
        <h2 className="text-sm font-semibold text-indigo-800">
          Training Available
        </h2>
        <p className="text-xs text-gray-700">Online Class | Physical Class</p>
        <button
          onClick={handleEnroll}
          className="mt-2 px-4 py-1 bg-orange-600 text-white text-xs rounded-lg hover:bg-orange-700 transition duration-300"
        >
          Enroll Now
        </button>
      </div>

      <div className="max-w-4xl mx-auto p-2 pt-16 pb-16">
        {/* Course Overview */}
        <div className="mb-2 border border-gray-200 rounded-lg">
          <h2
            onClick={() => toggleSection("courseOverview")}
            className="bg-gray-100 p-1 cursor-pointer flex justify-between items-center text-sm font-semibold text-gray-700 hover:bg-gray-200"
          >
            {syllabusData.courseOverview.title}
            <span>{expandedSections["courseOverview"] ? "▲" : "▼"}</span>
          </h2>
          {expandedSections["courseOverview"] && (
            <div className="p-1 bg-white text-gray-600 leading-tight">
              {syllabusData.courseOverview.content}
            </div>
          )}
        </div>

        {/* Daily Schedule */}
        <div className="mb-2 border border-gray-200 rounded-lg">
          <h2
            onClick={() => toggleSection("dailySchedule")}
            className="bg-gray-100 p-1 cursor-pointer flex justify-between items-center text-sm font-semibold text-gray-700 hover:bg-gray-200"
          >
            {syllabusData.dailySchedule.title}
            <span>{expandedSections["dailySchedule"] ? "▲" : "▼"}</span>
          </h2>
          {expandedSections["dailySchedule"] && (
            <div className="p-1 bg-white">
              {syllabusData.dailySchedule.days.map((day, index) => (
                <div
                  key={index}
                  className="mb-1 border border-gray-100 rounded-lg"
                >
                  <h3
                    onClick={() => toggleSection(`day${index}`)}
                    className="bg-gray-50 p-1 cursor-pointer flex justify-between items-center text-xs font-medium text-gray-600 hover:bg-gray-100"
                  >
                    {day.day}
                    <span>{expandedSections[`day${index}`] ? "▲" : "▼"}</span>
                  </h3>
                  {expandedSections[`day${index}`] && (
                    <div className="p-1 bg-white text-gray-600 leading-tight">
                      <p className="font-bold text-sm">Objective:</p>{" "}
                      {day.objective}
                      {day.topics}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Learning Outcomes */}
        <div className="mb-2 border border-gray-200 rounded-lg">
          <h2
            onClick={() => toggleSection("learningOutcomes")}
            className="bg-gray-100 p-1 cursor-pointer flex justify-between items-center text-sm font-semibold text-gray-700 hover:bg-gray-200"
          >
            {syllabusData.learningOutcomes.title}
            <span>{expandedSections["learningOutcomes"] ? "▲" : "▼"}</span>
          </h2>
          {expandedSections["learningOutcomes"] && (
            <div className="p-1 bg-white text-gray-600 leading-tight">
              {syllabusData.learningOutcomes.content}
            </div>
          )}
        </div>

        {/* Final Project Ideas */}
        <div className="mb-2 border border-gray-200 rounded-lg">
          <h2
            onClick={() => toggleSection("finalProjectIdeas")}
            className="bg-gray-100 p-1 cursor-pointer flex justify-between items-center text-sm font-semibold text-gray-700 hover:bg-gray-200"
          >
            {syllabusData.finalProjectIdeas.title}
            <span>{expandedSections["finalProjectIdeas"] ? "▲" : "▼"}</span>
          </h2>
          {expandedSections["finalProjectIdeas"] && (
            <div className="p-1 bg-white text-gray-600 leading-tight">
              {syllabusData.finalProjectIdeas.content}
            </div>
          )}
        </div>

        {/* Additional Notes */}
        <div className="mb-2 border border-gray-200 rounded-lg">
          <h2
            onClick={() => toggleSection("additionalNotes")}
            className="bg-gray-100 p-1 cursor-pointer flex justify-between items-center text-sm font-semibold text-gray-700 hover:bg-gray-200"
          >
            {syllabusData.additionalNotes.title}
            <span>{expandedSections["additionalNotes"] ? "▲" : "▼"}</span>
          </h2>
          {expandedSections["additionalNotes"] && (
            <div className="p-1 bg-white text-gray-600 leading-tight">
              {syllabusData.additionalNotes.content}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Oracle;